<template>
  <div>
    <PageAllow controller="Requisitions" :action="pageType == 'in' ? 'getPricingMaterials' : 'getOutPricingMaterials'" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              deleteIcon
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">
                {{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}
              </v-icon>
            </v-btn>
            <v-col>
              <!-- 批量核价导出 -->
              <v-btn
                small
                color="print"
                dark
                class="ms-1 float-end"
                @click="requisitionPricingTemplateExport()"
                v-if="
                  $permission.permissionAllow(
                    'Requisitions',
                    (pageType == 'in') ? 'inPricingExport' : 'outPricingExport'
                  )
                "
              >
                <v-icon x-small class="pe-1">fa-file-export</v-icon>
                {{ $t("OrdersPricing.export") }}
              </v-btn>
              <!-- 批量核价导入 -->
              <v-btn
                small
                color="print"
                dark
                class="ms-1 float-end"
                @click.stop="ImportDialog = true"
                v-if="
                  $permission.permissionAllow('Requisitions', (pageType == 'in') ? 'inPricingImport' : 'outPricingImport')
                "
              >
                <v-icon x-small class="pe-1">fa-file-import</v-icon>
                {{ $t("OrdersPricing.import") }}
              </v-btn>
              <!-- 改价记录 -->
              <v-btn
                small
                dark
                color="primary"
                class="ms-1 float-end"
                :href="pageType == 'in' ? '/#/requisitions/batch-pricing-log' : '/#/requisitions/batch-out-pricing-log'"
              >
                {{ $t("Requisition.pricingLog") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="order_code"
                  v-model="options.order_code"
                  @text="order_code = $event"
                  :label="$t('Requisition.Fields.requisition_code') + ' / ' +
                    (pageType == 'in' ? $t('Requisition.Fields.stock_in_order_code') : $t('Requisition.Fields.stock_out_order_code'))
                  "
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStock v-model="stockSelected" :stock-types="[0]" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="keyword"
                  v-model="options.keyword"
                  @text="keyword = $event"
                  :label="$t('Material.title')"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="materials"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          mobile-breakpoint="800"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:item.Description="{ item }">
            <a
              class="text-no-wrap"
              :class="item.error_flg ? 'red--text' : ''"
              @click.stop="
                currentDialog = true;
                currentItem = item;
              "
            >
              {{ item.material_name }}
            </a>
            <br />
            <span v-if="item.standard_name" class="caption secondary--text">
              {{ $t("Material.Fields.standard") }}{{ $t("Global.colon")}}
              <span v-if="item.standard_name && item.standard_name.length > 12" :title="item.standard_name" style="cursor: pointer">
                {{ item.standard_name.slice(0, 12) + "......" }}
              </span>
              <span v-else>
                {{ item.standard_name }}
              </span>
            </span>
          </template>
          <template v-slot:item.total_num="{ item }">
            {{ item.total_num | formatTwoDot }}
          </template>
        </v-data-table>
      </div>
    </v-sheet>
    <RequisitionListsDialog
      v-model="currentDialog"
      :searcherData="options"
      :currentItem="currentItem"
      :pageType="pageType"
    />
    <ImportExcelAlert
      v-model="ImportDialog"
      :objName="pageType == 'in' ? $t('Requisition.batchPricing') : $t('Requisition.outBatchPricing')"
      hideDownload
      @import="
        excelFile = $event;
        importExcel();
      "
    />
    <ImportPricingResult
      v-model="ImportResultDialog"
      :errorPricing="errorPricing"
      :pageType="pageType"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Requisitions from "@/api/requisitions";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherKeyword from "@/components/plugins/SearcherKeyword.vue";
import SearcherStock from "@/components/plugins/SearcherStock";
import RequisitionListsDialog from "./plugins/RequisitionListsDialog";
import ImportExcelAlert from "@/components/plugins/ImportExcelAlert";
import ImportPricingResult from "./plugins/ImportPricingResult";

const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime()).toISOString().substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  order_code: "",
  stock_id: [],
  keyword: "",
};

function getPageType() {
  let urlParts = document.URL.split('/');
  urlParts = urlParts[urlParts.length - 1]
  switch (urlParts) {
    default:
    case "batch-pricing":
      return 'in';
    case "out-batch-pricing":
      return 'out';
  }
}

function getOptionPrefix() {
  switch (getPageType()) {
    default:
    case 'in':
      return "requisitions-pricing";
    case 'out':
      return "requisitions-out-pricing";
  }
}

function getOptionSelects() {
  switch (getPageType()) {
    default:
    case 'in':
      return [
        "stockSelected"
      ];
    case 'out':
      return [
        "stockSelected"
      ];
  }
}

export default {
  components: {
    SearcherKeyword,
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    SearcherStock,
    RequisitionListsDialog,
    ImportExcelAlert,
    ImportPricingResult,
  },
  computed: {
    items() {
      let urlParts = document.URL.split('/');
      urlParts = urlParts[urlParts.length - 1]
      let items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.stockManagement"),
          disabled: false,
          replace: true,
          href: "/stocks",
        },
        {
          text: i18n.t("Nav.requisitions"),
          disabled: false,
          href: "/requisitions",
        },
      ];
      if (urlParts == 'batch-pricing') {
        items.push(
          {
            text: i18n.t("Requisition.batchPricing"),
            disabled: true,
            href: "/requisitions/batch-pricing",
          }
        )
      }else if (urlParts == 'out-batch-pricing'){
        items.push(
          {
            text: i18n.t("Requisition.outBatchPricing"),
            disabled: true,
            href: "/requisitions/out-batch-pricing",
          }
        )
      }
      return items;
    },
    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("Requisition.Fields.created_time"),
      },
    ],
    headers() {
      return [
        {
          text: i18n.t("Requisition.DetailFields.material_code"),
          align: "start",
          sortable: false,
          value: "material_code",
          width: 300,
        },
        {
          text: i18n.t("Requisition.DetailFields.material_name"),
          align: "start",
          sortable: false,
          value: "Description",
          width: 380,
        },
        {
          text: i18n.t("Requisition.DetailFields.material_category"),
          align: "start",
          sortable: false,
          value: "category_name",
        },
        {
          text: i18n.t("Requisition.DetailFields.total_num"),
          align: "start",
          sortable: false,
          value: "total_num",
        },
        {
          text: i18n.t("Requisition.DetailFields.unit"),
          align: "start",
          sortable: false,
          value: "unit",
        },
      ];
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },
  data: () => ({
    order_code: null,
    stockSelected: null,
    keyword: null,
    options: JSON.parse(
      sessionStorage.getItem(getOptionPrefix() + "-options")
        ? sessionStorage.getItem(getOptionPrefix() + "-options")
        : JSON.stringify(defaultOptions)
    ),
    paging: {},
    materials: [],
    currentDialog: false,
    currentItem: {},
    ImportDialog: false,
    excelFile: null,
    ImportResultDialog: false,
    errorPricing: [],
    pageType: getPageType(),
  }),

  mounted() {
    this.resetPage()
  },

  methods: {
    resetSearch() {
      this.stockSelected = null;
      [this.$refs.order_code, this.$refs.keyword].forEach((k) => {
        if (k) k.clear();
      });
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.options = Object.assign({},this.options, {
        order_code: this.order_code,
        keyword: this.keyword,
      });
    },
    getPricingMaterials() {
      Requisitions.getPricingMaterials(this.options, this.pageType).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.materials = response.data.requisition_materials;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    requisitionPricingTemplateExport() {
      Requisitions.requisitionPricingTemplateExport(this.options, this.pageType);
    },
    importExcel() {
      this.$store.dispatch("fullscreenLoading");
      Requisitions.orderPricingImport(this.excelFile, this.pageType)
        .then(
          function (response) {
            if (response.data.status.isOK()) {
              this.ImportDialog = false;
              if (response.data.error_materials && response.data.error_materials.length > 0) {
                this.ImportResultDialog = true;
                this.errorPricing = response.data.error_materials;
              } else {
                this.$snackbar.info(i18n.t("Import.success", [i18n.t("OrdersPricing.ObjectName")]));
              }
            }
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        )
        .catch(
          function () {
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        );
    },
    resetPage() {
      this.pageType = getPageType();
      this.$commonUtils.loadOptions(getOptionPrefix(), getOptionSelects()).then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();
          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "stockSelected":
                this.stockSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
    },
  },

  watch: {
    $route: {
      handler() {
        this.resetPage()
      },
    },
    options: {
      deep: true,
      handler() {
        this.getPricingMaterials();
        sessionStorage.setItem(
            getOptionPrefix() + "-options",
          JSON.stringify(this.options)
        );
      },
    },
    stockSelected: {
      handler() {
        this.options.stock_id = this.stockSelected
          ? this.stockSelected.id
          : null;
        sessionStorage.setItem(
            getOptionPrefix() + "-stockSelected",
          JSON.stringify(this.stockSelected)
        );
      },
    },
  },
};
</script>